import appInfo from "@common/appInfo";
import RichEditor, { type RichEditorProps } from "@common/components/Inputs/RichEditor";
import CountrySelect from "@common/components/Inputs/Select/Country";
import RegionFormField from "@common/controls/Form/Field/Region";
import FormField, { type FormFieldProps } from "@common/controls/Form/Field/component";
import { fieldValidation } from "@common/controls/Form/Field/lib";
import { FormGroup, type FormRequiredMode } from "@common/controls/Form/component";
import { type CheckboxProps, FancyCheckbox } from "@common/controls/Inputs/Checkbox/component";
import DateRangePicker, {
  type DateRangePickerProps,
  type DateRangeValue
} from "@common/controls/Inputs/DateRangePicker/component";
import DateTimePicker from "@common/controls/Inputs/DateTimePicker/component";
import MoneyInput, { type MoneyInputProps } from "@common/controls/Inputs/Number/Money";
import PercentInput from "@common/controls/Inputs/Number/Percent";
import NumberInput, { type NumberInputProps } from "@common/controls/Inputs/Number/component";
import PhoneInput, { type PhoneInputProps } from "@common/controls/Inputs/PhoneInput/component";
import { DynamicRadioButtonGroup, RadioButtonGroup } from "@common/controls/Inputs/Radio";
import AutoComplete, {
  AutoCompleteLazy,
  DynamicAutoComplete
} from "@common/controls/Inputs/Select/AutoComplete/component";
import MultiSelect, {
  DynamicMultiSelect,
  type MultiSelectOutputType
} from "@common/controls/Inputs/Select/MultiSelect/component";
import Select, { DynamicSelect, type OptionItem } from "@common/controls/Inputs/Select/component";
import { EmailInput, TextInputSensitive, type TextInputSensitiveProps } from "@common/controls/Inputs/Text";
import PlainText from "@common/controls/Inputs/Text/Plain";
import TextInput, { type TextInputProps } from "@common/controls/Inputs/Text/component";
import { getInputInfo } from "@common/controls/Inputs/Text/lib";
import TextArea, { type TextAreaProps } from "@common/controls/Inputs/TextArea/component";
import { type Api, type GenericMap, IconType, type SortOrder } from "@common/declarations";
import { type FetchOptions, type FetchOptionsArgs } from "@common/hocs/withLazy";
import { mapV1DropdownItem, toOptionItems } from "@common/lib/api";
import * as format from "@common/lib/format";
import { type I18n, localize } from "@common/lib/i18n";
import { EMPTY_OBJECT, isVisible } from "@common/lib/misc";
import getOptions from "@common/lib/options";
import { getTokenName, isToken } from "@common/lib/token";
import { parseQueryString } from "@common/lib/url";
import * as validate from "@common/lib/validate";
import { type FormControlKey } from "@common/modules/system/types";
import { Common } from "@common/types";
import { groupBy, isFunction, isNull, isUndefined, sortBy, toPairs, trim } from "lodash";
import moment from "moment";
import React, { type ReactNode } from "react";
import { type DynamicForms } from "./types";

export const ICONS_MAP = {
  FirstName: "far fa-user",
  LastName: "far fa-user",
  Telephone: "far fa-phone",
  Street: "far fa-map-marker-alt",
  City: "far fa-map-marker-alt",
  Country: "far fa-map-marker-alt",
  PostalCode: "far fa-mail-bulk",
  PreferredTimeZone: "far fa-clock",
  PreferredLocale: "far fa-language",
  Region: "far fa-map-marker-alt",
  Unit: "far fa-map-marker-alt",
  Company: "far fa-user",
  Password: "far fa-unlock-alt",
  ConfirmPassword: "far fa-unlock-alt",
  ReferringAffiliateUserId: "far fa-user",
  Text: "far fa-align-left"
};

export const getFormControlIcon = (type: DynamicForms.FormControlKey): IconType => {
  switch (type) {
    case "Autocomplete":
      return IconType.ListDropdown;
    case "Checkbox":
      return IconType.SuqareCheck;
    case "Currency":
      return IconType.DollarSign;
    case "Date":
      return IconType.Calendar;
    case "DateTime":
      return IconType.CalendarClock;
    case "Email":
      return IconType.Envelope;
    case "Integer":
      return IconType.Hashtag;
    case "Number":
      return IconType.Hashtag;
    case "RadioButtonGroup":
      return IconType.ListRadio;
    case "RegionSelect":
      return IconType.EarthAmericas;
    case "RichText":
      return IconType.AlignLeft;
    case "Select":
      return IconType.ListDropdown;
    case "SouthAfricanIdNumber":
      return IconType.Hashtag;
    case "Telephone":
      return IconType.Phone;
    case "Text":
      return IconType.AlignLeft;
    case "TextArea":
      return IconType.AlignLeft;
    case "Time":
      return IconType.Clock;
  }
  return IconType.AlignLeft;
};

export const getFormControlLabel = (i18n: I18n, type: DynamicForms.FormControlKey): string => {
  switch (type) {
    case "Autocomplete":
      return i18n.autocomplete;
    case "Checkbox":
      return i18n.checkbox;
    case "Currency":
      return i18n.currency;
    case "Date":
      return i18n.date;
    case "DateRange":
      return i18n.dateRange;
    case "DateTime":
      return i18n.dateTime;
    case "Email":
      return i18n.email;
    case "Hidden":
      return i18n.hidden;
    case "Integer":
      return i18n.integer;
    case "Multiselect":
      return i18n.multiselect;
    case "Number":
      return i18n.number;
    case "Password":
      return i18n.password;
    case "RadioButtonGroup":
      return i18n.radioButtonGroup;
    case "RegionMultiselect":
      return i18n.regionMultiselect;
    case "RegionSelect":
      return i18n.regionSelect;
    case "RichText":
      return i18n.richText;
    case "Select":
      return i18n.select;
    case "Sensitive":
      return i18n.sensitive;
    case "SouthAfricanIdNumber":
      return i18n.southAfricanIdNumber;
    case "Telephone":
      return i18n.phone;
    case "Text":
      return i18n.text;
    case "TextArea":
      return i18n.textArea;
    case "Time":
      return i18n.time;
    default:
      return type;
  }
};

const getSelectComponent = (fck: DynamicForms.FormControlKey, source: SourceInfo): any => {
  const isDynamic = !!source.fetchOptions;
  switch (fck) {
    case "Autocomplete":
      return isDynamic ? (source.isBePagination ? AutoCompleteLazy : DynamicAutoComplete) : AutoComplete;
    case "Multiselect":
      return isDynamic ? DynamicMultiSelect : MultiSelect;
    case "RadioButtonGroup":
      return isDynamic ? DynamicRadioButtonGroup : RadioButtonGroup;
    case "Select":
      return isDynamic ? DynamicSelect : Select;
    default:
      throw new Error(`Unsupported select form control key '${fck}'`);
  }
};

export const isFieldVisible = (visibilityCondition: string | undefined | null, values: GenericMap<any>): boolean =>
  isVisible(visibilityCondition, values);

export type DynamicFormMapOptions = {
  additionalProps?: Partial<FormFieldProps<any, any>> | ((name: string) => Partial<FormFieldProps<any, any>>);
  errors?: GenericMap<string | null>;
  onChange?: (name: string, value: any) => void;
  multiSelectOutput?: MultiSelectOutputType;
  requiredMode?: FormRequiredMode;

  getComponentProps?: (field: DynamicForms.Field) => any;
};

export const mapFieldBaseProps = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
): Omit<FormFieldProps<any, any>, "Component"> => {
  return {
    colSpan: field.width,
    disabledTooltip: field.disabledTooltip,
    hint: field.description,
    hintMode: field.descriptionMode,
    isRequired: field.isRequired,
    isDisabled: field.isDisabled,
    isNoStyle: field.formControlKey === "RadioButtonGroup" || field.formControlKey === "Checkbox",
    isTrueRequired: field.formControlKey === "Checkbox",
    label: field.label,
    name: field.name,
    onChange: opts?.onChange,
    selectBehaviour: field.selectBehaviour,
    tooltip: field.tooltip,
    validator: async (i18n, value) => {
      if (isFunction(field.validations)) return field.validations(i18n, value);

      const primaryValidator = fieldValidation.getPrimary(field.formControlKey);
      if (primaryValidator) {
        const error = await primaryValidator(i18n, value);
        if (error) return error;
      }

      return (field.validations ?? []).reduce(
        (res, x) => res ?? validate.validateValue(value, x, field.formControlKey),
        null
      );
    },
    value: mapFieldValue(field.name in values ? values[field.name] : field.value, field.formControlKey, field.dataType)
  };
};

export const isFreeInput = (formControlKey: FormControlKey): boolean => {
  switch (formControlKey) {
    case "Email":
    case "Integer":
    case "Number":
    case "RichText":
    case "Text":
    case "TextArea": {
      return true;
    }
    default:
      return false;
  }
};

const mapField = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
): FormFieldProps<any, any> | null => {
  if (!isFieldVisible(field.visibilityCondition, values)) return null;

  const baseProps = mapFieldBaseProps(i18n, field, values, opts);
  const componentBaseProps = opts?.getComponentProps
    ? opts.getComponentProps(field)
    : field.componentProps ?? EMPTY_OBJECT;
  switch (field.formControlKey) {
    case "Checkbox": {
      const { label, ...cbProps } = baseProps;
      return {
        ...cbProps,
        Component: FancyCheckbox,
        componentProps: {
          ...componentBaseProps,
          label: opts?.requiredMode === "star" && !!baseProps.isRequired ? `${baseProps.label} *` : baseProps.label
        } as Partial<CheckboxProps>,
        // TODO: nicer solution?
        validator: (x) => (baseProps.isRequired && !x ? i18n.errorRequiredField : null)
      };
    }
    case "Country": {
      return {
        ...baseProps,
        Component: CountrySelect
      };
    }
    case "Currency": {
      return {
        ...baseProps,
        Component: MoneyInput,
        componentProps: {
          ...componentBaseProps,
          allowEmpty: true,
          fractionalDigits: getFractionalDigits(field.dataType)
        } as Partial<MoneyInputProps>
      };
    }
    case "Date":
    case "DateTime": {
      return {
        ...baseProps,
        Component: DateTimePicker,
        componentProps: {
          ...componentBaseProps,
          hasTime: field.formControlKey === "DateTime"
        }
      };
    }
    case "DateRange": {
      return {
        ...baseProps,
        Component: DateRangePicker,
        componentProps: {
          ...componentBaseProps,
          outputType: "string"
        } as DateRangePickerProps
      };
    }
    case "Email": {
      return {
        ...baseProps,
        Component: EmailInput,
        componentProps: {
          ...componentBaseProps
        },
        validator: baseProps.validator ?? validate.validators.email
      };
    }
    case "Integer": {
      return {
        ...baseProps,
        Component: NumberInput,
        componentProps: {
          ...componentBaseProps,
          allowEmpty: true,
          fractionalDigits: 0
        } as NumberInputProps
      };
    }
    case "Number": {
      return {
        ...baseProps,
        Component: NumberInput,
        componentProps: {
          ...componentBaseProps,
          allowEmpty: true,
          fractionalDigits: getFractionalDigits(field.dataType)
        } as Partial<NumberInputProps>
      };
    }
    case "RichText": {
      const cp: Partial<RichEditorProps> = {
        toolbar: ["bold", "italic", "underline", "a"]
      };
      return {
        ...baseProps,
        Component: RichEditor,
        componentProps: {
          ...componentBaseProps,
          ...cp
        } as Partial<RichEditorProps>
      };
    }
    case "Autocomplete":
    case "Multiselect":
    case "RadioButtonGroup":
    case "Select": {
      const source = parseSource(i18n, field.source, field.options, values);

      return {
        ...baseProps,
        Component: getSelectComponent(field.formControlKey, source),
        componentProps: {
          ...componentBaseProps,
          ...source,
          showLabel: true,

          ...(field.formControlKey === "Multiselect"
            ? {
                outputType: opts?.multiSelectOutput
              }
            : {}),
          ...(field.formControlKey === "Autocomplete"
            ? {
                fetchDefaultOption: field.fetchDefaultOption,
                defaultSearchText: !field.fetchDefaultOption ? field.valuePlain ?? field.value : null
              }
            : {}),
          ...(field.formControlKey === "RadioButtonGroup"
            ? {
                orientation: "vertical"
              }
            : {})
        }
      };
    }
    case "PlainText": {
      return {
        ...baseProps,
        Component: PlainText
      };
    }
    case "Percent": {
      return {
        ...baseProps,
        Component: PercentInput,
        componentProps: componentBaseProps
      };
    }
    case "Password":
    case "Sensitive": {
      return {
        ...baseProps,
        Component: TextInputSensitive,
        componentProps: {
          ...componentBaseProps
        } as Partial<TextInputSensitiveProps>
      };
    }
    case "SouthAfricanIdNumber": {
      return {
        ...baseProps,
        Component: TextInput,
        componentProps: {
          ...componentBaseProps
        }
      };
    }
    case "Telephone": {
      return {
        ...baseProps,
        Component: PhoneInput,
        componentProps: {
          ...componentBaseProps,
          countryCode: appInfo.countryCode
        } as Partial<PhoneInputProps>
      };
    }
    case "Hidden":
    case "Text": {
      return {
        ...baseProps,
        Component: TextInput,
        componentProps: {
          ...componentBaseProps,
          ...getInputInfo(field.name),
          type: field.formControlKey === "Hidden" ? "hidden" : "text"
        } as Partial<TextInputProps>
      };
    }
    case "TextArea": {
      return {
        ...baseProps,
        Component: TextArea,
        componentProps: {
          ...componentBaseProps
        } as Partial<TextAreaProps>
      };
    }
    default:
      throw new Error(`Unsupported form control key '${field.formControlKey}'`);
  }
};

export const mapFormFieldInner = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
): { field: any; colSpan: number } => {
  const additionalProps = isFunction(opts?.additionalProps) ? opts.additionalProps(field.name) : opts?.additionalProps;

  switch (field.formControlKey) {
    case "RegionSelect":
    case "RegionMultiselect": {
      const bp = mapFieldBaseProps(i18n, field, values, opts);
      if (!bp) return { field: null, colSpan: 0 };

      return {
        field: (
          <RegionFormField
            {...bp}
            key={bp.name}
            isMultiselect={field.formControlKey === "RegionMultiselect"}
            {...additionalProps}
          />
        ),
        colSpan: bp.colSpan ?? 12
      };
    }
    default: {
      const fp = mapField(i18n, field, values, opts);
      if (!fp) return { field: null, colSpan: 0 };

      return {
        field: (
          <FormField
            {...fp}
            key={fp.name}
            errorMessage={opts?.errors ? opts?.errors[field.name] : undefined}
            qa={field.qa}
            {...additionalProps}
          />
        ),
        colSpan: fp.colSpan ?? 12
      };
    }
  }
};

export const mapFormField = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
): ReactNode => {
  const { field: ff } = mapFormFieldInner(i18n, field, values, opts);
  return ff;
};

/**
 * Maps to a more handy alternative field. E.g. radio button group -> select, checkbox -> select with 'Checked'/'Unchecked' options etc.
 */
export const mapFormFieldDesign = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
): any => {
  const finalField = { ...field };
  switch (field.formControlKey) {
    case "Checkbox": {
      finalField.formControlKey = "Select";
      finalField.options = [
        { caption: i18n.checked, value: "true" },
        { caption: i18n.unchecked, value: "false" }
      ];
      finalField.source = SOURCE_OPTIONS;
      break;
    }
    case "RadioButtonGroup": {
      finalField.formControlKey = "Select";
      break;
    }
    case "TextArea": {
      finalField.formControlKey = "Text";
      break;
    }
  }

  return mapFormField(i18n, finalField, values, opts);
};

export const getDefaultValues = <T extends {}>(fields: DynamicForms.Field[]): T =>
  fields.reduce(
    (res, f) => ({
      ...res,
      [f.name]: mapFieldValue(f.value, f.formControlKey, f.dataType)
    }),
    {}
  ) as T;

export type MapFormFieldInnerFn = (
  i18n: I18n,
  field: DynamicForms.Field,
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions
) => { field: ReactNode; colSpan: number };

export const mapFormElements = (
  i18n: I18n,
  fields: DynamicForms.Field[],
  values: GenericMap<any>,
  opts?: DynamicFormMapOptions,
  mapFormField?: MapFormFieldInnerFn
): ReactNode[] => {
  const sortedFields = sortBy(
    fields.filter((x) => x.formControlKey !== "Hidden" && !x.isHidden),
    (f) => f.sortOrder
  );

  const fieldsWithGroup = sortedFields.map((x, i) => ({ ...x, group: x.group ?? i }));
  const byGroup = groupBy(fieldsWithGroup, (x) => x.group);

  return Object.keys(byGroup).map((x) => (
    <FormGroup key={x}>
      {byGroup[x].map((y) => {
        const { field } = (mapFormField ?? mapFormFieldInner)(i18n, y, values, opts);
        return field;
      })}
    </FormGroup>
  ));
};

type SourceInfo = {
  dependent?: any;
  fetchOptions?: (() => Promise<OptionItem<any>[]>) | FetchOptions<OptionItem<any>>;
  isBePagination: boolean;
  options?: OptionItem<any>[];
};
const EMPTY_SOURCE: SourceInfo = {
  isBePagination: false,
  options: []
};

export const FIXED_SOURCES: GenericMap<(i18n: I18n) => OptionItem<any>[]> = {
  yesNo: (i18n: I18n) => getOptions(i18n).yesNo(),
  enabledDisabled: (i18n: I18n) => getOptions(i18n).enabledDisabled()
};
export const SOURCE_OPTIONS = "OPTIONS";
export const parseSource = (
  i18n: I18n,
  source: string | null | undefined,
  options: Common.Api.DropdownItem<string>[] | undefined,
  values: GenericMap<any>
): SourceInfo => {
  if (!source) return EMPTY_SOURCE;

  if (source === SOURCE_OPTIONS) {
    return {
      isBePagination: false,
      options: toOptionItems((options ?? []).map(mapV1DropdownItem))
    };
  }

  const m = source.match(/([^:]+)\:(.+)/);
  if (!m) return EMPTY_SOURCE;

  switch (m[1]) {
    case "api": {
      const parts = m[2].split("|").map((x) => x.trim());
      const parameters =
        parts[2] && !parts[2].match(/(asc)|(desc)/)
          ? toPairs(parseQueryString(`?${parts[2]}`)).reduce(
              (res, p: any[]) => ({
                ...res,
                [p[0]]: p[1]
                  ? isToken(p[1])
                    ? values[getTokenName(p[1])]
                    : adjustSourceParamValue(p[1])
                  : values[p[0]]
              }),
              {}
            )
          : {};

      const isBePagination = parameters && "page" in parameters;

      const optionsLib = getOptions(i18n);
      return {
        dependent: JSON.stringify(parameters),
        isBePagination,
        fetchOptions: isBePagination
          ? (x: FetchOptionsArgs) =>
              optionsLib.getOptionsPage(parts[0], parts[1], {
                ...parameters,
                ...x,
                order:
                  parts[2] === "asc" || parts[2] === "desc" ? parts[2] : (parts[3] as Partial<SortOrder>) || undefined
              })
          : optionsLib.getGetOptions(
              parts[0],
              parts[1],
              parameters,
              parts[2] === "asc" || parts[2] === "desc" ? parts[2] : (parts[3] as Partial<SortOrder>) || undefined
            )
      };
    }
    case "fixed": {
      const fs = FIXED_SOURCES[m[2]];
      return fs
        ? {
            isBePagination: false,
            options: fs(i18n)
          }
        : EMPTY_SOURCE;
    }
    case "json": {
      const options = JSON.parse(trim(m[2], '"')) as Api.DropdownItem[];
      localize(i18n, options);
      return {
        isBePagination: false,
        options: toOptionItems(options.map(mapV1DropdownItem))
      };
    }
    default:
      return EMPTY_SOURCE;
  }
};
export const mapFieldValue = (
  value: any,
  formControlKey: DynamicForms.FormControlKey,
  dataType?: string | null
): any => {
  switch (formControlKey) {
    case "Date":
    case "DateTime": {
      if (!value) return null;

      if (typeof value === "string") {
        if (value === "Date" || value === "Today" || value === "Now") return moment.utc().toDate();

        const dm = value.match(/(-|\+)(\d+)(months|years|days|hours)/i);
        if (dm) {
          const now = moment.utc();

          return now.add(+dm[2] * (dm[1] === "-" ? -1 : 1), dm[3].toLowerCase() as any).toDate();
        }
      }

      return moment(value);
    }
    case "DateRange": {
      if (!value) return null;

      if (moment.isMoment(value)) return null;
      if (typeof value === "object") {
        if ("from" in value && "to" in value) return value;
        return null;
      }
      try {
        const v = JSON.parse(value.toString());
        return {
          from: new Date(v.from),
          to: new Date(v.to)
        };
      } catch {
        return null;
      }
    }
    default: {
      if (value) {
        if (value.toString().toLowerCase() === "@currentuserid") return appInfo.user?.id;
        if (value.toString().toLowerCase() === "@currencycode") return appInfo.currencyCode;
      }
      return castType(value, dataType);
    }
  }
};

const adjustSourceParamValue = (param: string): any => {
  if (isNull(param) || isUndefined(param)) return param;
  if (typeof param === "string")
    return param
      .toString()
      .replace(/\@CurrentUserId/i, appInfo.user?.id ? appInfo.user.id.toString() : "")
      .replace(/\@CurrencyCode/i, appInfo.currencyCode);

  return param;
};

export const getFractionalDigits = (dataType?: string | null): number | undefined => {
  if (!dataType) return;
  if (dataType === "int") return 0;
  const decimalMatch = dataType.match(/^decimal\((\d+),\s*(\d+)\)$/);
  if (decimalMatch) return +decimalMatch[2];
  return;
};

export const castType = (value: any, dataType?: "int" | "bool" | "boolean" | "date" | string | null): any => {
  if (isUndefined(value) || isNull(value) || !dataType) return value;

  switch (dataType) {
    case "int":
      return +value;
    case "bool":
    case "boolean":
      return value.toString().toLowerCase() === "true";
    case "date":
      return new Date(value);
    default: {
      if (dataType.startsWith("decimal")) return +value;
      return value;
    }
  }
};

export const formatDisplayValue = (value: any, formControlKey: DynamicForms.FormControlKey | null) => {
  if (!formControlKey) return value;

  const mappedValue = mapFieldValue(value, formControlKey);
  switch (formControlKey) {
    case "Currency":
      return format.formatDisplayValue(mappedValue, "money");
    case "Date": {
      return format.formatDisplayValue(mappedValue, "dateLocal");
    }
    case "DateRange": {
      const dr = mappedValue as DateRangeValue;
      return `${format.formatDisplayValue(dr.from, "dateLocal")} - ${format.formatDisplayValue(dr.to, "dateLocal")}`;
    }
    case "DateTime": {
      return format.formatDisplayValue(mappedValue, "dateTimeLocal");
    }
    case "Percent": {
      return format.formatDisplayValue(mappedValue, "percent");
    }
    default:
      return mappedValue;
  }
};
